import React, { Children, cloneElement } from "react";

import cx from "classnames";

import { Fieldset } from "../Fieldset";
import { Flex, FlexChild } from "../Flex";
import { Spacer } from "../Grid";
import { Icon } from "../Icon";
import { Text } from "../Text";

import { THEME_COLORS } from "../../theme/themeTokens";

import type { RadioProps } from "./Radio";
import styles from "./Radio.scss";

/**
 * Props for the HelperText component
 */
interface HelperTextProps {
  /** The content to display */
  content?: React.ReactNode;
  /** The class name string to apply */
  classNameString: string;
}

/**
 * Props for the RadioGroup component
 */
export interface RadioGroupProps<TType extends string | number = string> {
  /** The Radio components to render */
  children: React.ReactNode;
  /** The currently selected radio ID */
  selected?: TType | null;
  /** Callback when a radio is selected */
  onChange: (id: TType) => void;
  /** Whether to display radios horizontally */
  horizontal?: boolean;
  /** Whether the radio group is disabled */
  disabled?: boolean;
  /** Label for the fieldset */
  label?: React.ReactNode;
  /** Helper text to display under the label */
  helperText?: React.ReactNode;
  /** Error text to display under the label */
  error?: string | React.ReactNode | null;
  /** Test ID for testing purposes */
  "data-testid"?: string;
  /** Optional label text to mark this element as optional */
  optionalLabelText?: string | React.ReactNode;
  /** Variant for the fieldset styling */
  fieldsetVariant?: "highlighted" | "transparent";
  /** Tooltip content */
  tooltip?: string | React.ReactNode;
  /** Whether to allow children to be disabled */
  allowChildrenToBeDisabled?: boolean;
}

/**
 * A helper component to render helper text or error messages
 */
function HelperText({
  content,
  classNameString,
}: HelperTextProps): JSX.Element | null {
  if (!content) {
    return null;
  }

  return (
    <Text size="paragraphXsmall" paragraph className={styles[classNameString]}>
      {content}
    </Text>
  );
}

type RadioComponent = React.ComponentType<RadioProps> & {
  displayName?: string;
};

/**
 * A radio control wrapper that manages a group of Radio components
 *
 * Notes:
 * - If no data-testid is passed to the child `Radio` components one will be auto-generated
 *   by a 0 based incrementing index at the end of the `RadioGroup` data-testid.
 */
export function RadioGroup<TType extends string | number = string>({
  children,
  error,
  selected,
  onChange,
  horizontal,
  label,
  disabled,
  helperText,
  "data-testid": dataTestId = "confetti-radio-group",
  optionalLabelText,
  fieldsetVariant,
  tooltip,
  allowChildrenToBeDisabled,
}: RadioGroupProps<TType>): JSX.Element {
  const radioCount = React.Children.count(children) - 1;
  const className = cx({
    [styles["horizontal"]]: horizontal,
    [styles["group-of-radios"]]: label,
  });

  const cloneChildren = () => {
    return Children.map(children, (child, i) => {
      if (!React.isValidElement(child)) {
        return child;
      }

      // child has its own margin style
      const childHasHelperText = Boolean(child.props.helperText);
      const isLast = i === radioCount;

      const classes = cx(
        child.props.className,
        {
          [styles.radioSpace]: !isLast && !childHasHelperText,
        },
        styles["radio-isgrouped"]
      );

      const testId = child.props["data-testid"]
        ? child.props["data-testid"]
        : `${dataTestId}-${i}`;

      // renders non-Radio children without Radio props
      const componentType = child.type as RadioComponent; // eslint-disable-line @typescript-eslint/consistent-type-assertions
      if (!componentType.displayName || componentType.displayName !== "Radio") {
        return cloneElement(child);
      }

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return cloneElement(child, {
        disabled:
          disabled || (allowChildrenToBeDisabled && child.props.disabled),
        checked: child.props.id === selected,
        onChange: (id: TType) => id !== selected && onChange(id),
        className: classes,
        "data-testid": testId,
        hasError: error ? String(error).trim() !== "" : false,
      } as Partial<RadioProps>);
    });
  };

  const errorContent = error ? String(error) : undefined;
  const tooltipContent = tooltip
    ? typeof tooltip === "string"
      ? tooltip
      : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        (tooltip as JSX.Element)
    : undefined;
  const optionalText = optionalLabelText
    ? typeof optionalLabelText === "string"
      ? optionalLabelText
      : undefined
    : undefined;

  return label ? (
    <Fieldset
      data-testid={dataTestId}
      label={label}
      disabled={disabled}
      legendType="radiogroup"
      optionalLabelText={optionalText}
      variant={fieldsetVariant}
      tooltip={tooltipContent}
    >
      <Flex alignItems="center">
        {error && (
          <>
            <FlexChild>
              <Icon icon="warning" size={12} color={THEME_COLORS.statusError} />
              <Spacer size="small" />
            </FlexChild>
            <Spacer size="tiny" orientation="horizontal" />
          </>
        )}
        <HelperText
          content={errorContent}
          classNameString="group-helper-text--error"
        />
      </Flex>
      <HelperText content={helperText} classNameString="group-helper-text" />
      <div className={className}>{cloneChildren()}</div>
    </Fieldset>
  ) : (
    <div data-testid={dataTestId}>
      <Flex alignItems="center">
        {error && (
          <>
            <FlexChild>
              <Icon icon="warning" size={12} color={THEME_COLORS.statusError} />
              <Spacer size="small" />
            </FlexChild>
            <Spacer size="tiny" orientation="horizontal" />
          </>
        )}
        <HelperText
          content={errorContent}
          classNameString="group-helper-text--error"
        />
      </Flex>
      <HelperText content={helperText} classNameString="group-helper-text" />
      <div className={className}>{cloneChildren()}</div>
    </div>
  );
}
